import { Warranty } from '@api/types';
import { useWarrantyClaimContext } from '@components';
import { FormControl, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { forwardRef } from 'react';
import { Controller } from 'react-hook-form';

type WarrantyDateFieldProps = {
  label: string;
  name: keyof Warranty;
  required?: boolean;
  minDate?: string;
  validate?: (value: string, formState: keyof Warranty) => boolean | string;
};

export const WarrantyDateField = forwardRef<HTMLDivElement, WarrantyDateFieldProps>(
  ({ label, name, required, minDate, validate }, ref) => {
    const { form, isReadOnly } = useWarrantyClaimContext();
    const isError = !!form.formState.errors[name];
    const theme = useTheme();

    const getValue = () => {
      return form.watch(name) ? dayjs(form.watch(name)?.toString(), 'YYYY-MM-DD') : null;
    };

    return (
      <Controller
        control={form.control}
        name={name}
        rules={{
          required: required,
          validate: (value) => (validate ? validate(value, name) : undefined),
        }}
        render={({ field, fieldState: { error } }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormControl sx={{ width: '100%' }} error={isError} required={required}>
              <DatePicker
                ref={ref}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1197 },
                  },
                  textField: {
                    placeholder: 'DD/MM/YYYY',
                    helperText: isError ? error?.message : undefined,
                  },
                }}
                label={label}
                sx={{
                  mt: 5,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isError ? theme.palette.error.main : undefined,
                  },
                  '& .MuiFormLabel-root': {
                    color: isError ? theme.palette.error.main : undefined,
                  },
                }}
                format='DD/MM/YYYY'
                value={getValue()}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    field.onChange(dayjs(newValue).format('YYYY-MM-DD'));
                  } else {
                    field.onChange(undefined);
                  }
                }}
                disableFuture
                readOnly={isReadOnly}
                minDate={dayjs(minDate, 'YYYY-MM-DD')}
              />
            </FormControl>
          </LocalizationProvider>
        )}
      />
    );
  }
);
